/**
 * Period locks. Append-only: a lock is never lifted; bookings dated on or
 * before the latest `lockedThrough` are refused by `validateVerifikation`
 * and corrections go to the first open day.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { IsoDate, PeriodLock, PeriodLockReason, Uuid } from '../types';
import { firstOpenDate } from './deadlines';

export { firstOpenDate };

export class PeriodLockError extends Error {
  constructor(
    public readonly code: 'fiscal_year_missing' | 'not_in_fiscal_year' | 'already_locked' | 'lock_moves_backwards',
    message: string,
  ) {
    super(message);
    this.name = 'PeriodLockError';
  }
}

export interface LockPeriodInput {
  companyId: Uuid;
  fiscalYearId: Uuid;
  periodKey: string;
  lockedThrough: IsoDate;
  reason: PeriodLockReason;
  settlementVerifikationId?: Uuid;
}

/** Latest lockedThrough among a set of locks; undefined when none. */
export function latestLockedThrough(locks: readonly Pick<PeriodLock, 'lockedThrough'>[]): IsoDate | undefined {
  let latest: IsoDate | undefined;
  for (const lock of locks) if (!latest || lock.lockedThrough > latest) latest = lock.lockedThrough;
  return latest;
}

/** True when `date` falls in a locked period. */
export function isLockedDate(date: IsoDate, lockedThrough: IsoDate | undefined): boolean {
  return lockedThrough !== undefined && date <= lockedThrough;
}

export async function listPeriodLocks(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<PeriodLock[]> {
  const locks = await db.periodLocks.where('companyId').equals(companyId).toArray();
  return locks.sort((a, b) => a.lockedThrough.localeCompare(b.lockedThrough) || a.at.localeCompare(b.at));
}

export async function lockedThroughFor(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<IsoDate | undefined> {
  return latestLockedThrough(await db.periodLocks.where('companyId').equals(companyId).toArray());
}

/**
 * Append a period lock and log it as an `accountEvents` row of kind
 * `period_locked` in the same transaction. Locks only move forward.
 */
export async function lockPeriod(input: LockPeriodInput, db: BookkeepingDB = defaultDb): Promise<PeriodLock> {
  return db.transaction('rw', db.periodLocks, db.accountEvents, db.fiscalYears, async () => {
    const fy = await db.fiscalYears.get(input.fiscalYearId);
    if (!fy || fy.companyId !== input.companyId) {
      throw new PeriodLockError('fiscal_year_missing', 'Räkenskapsåret finns inte');
    }
    if (input.lockedThrough < fy.startDate || input.lockedThrough > fy.endDate) {
      throw new PeriodLockError('not_in_fiscal_year', 'Låsdatumet ligger utanför räkenskapsåret');
    }
    const existing = await db.periodLocks.where('companyId').equals(input.companyId).toArray();
    if (existing.some((l) => l.periodKey === input.periodKey && l.reason === input.reason)) {
      throw new PeriodLockError('already_locked', 'Perioden är redan låst');
    }
    const current = latestLockedThrough(existing);
    if (current !== undefined && input.lockedThrough < current) {
      throw new PeriodLockError('lock_moves_backwards', `Bokföringen är redan låst till och med ${current}`);
    }
    const now = new Date().toISOString();
    const lock: PeriodLock = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      fiscalYearId: input.fiscalYearId,
      periodKey: input.periodKey,
      lockedThrough: input.lockedThrough,
      reason: input.reason,
      at: now,
      ...(input.settlementVerifikationId ? { settlementVerifikationId: input.settlementVerifikationId } : {}),
    };
    await db.periodLocks.add(lock);
    await db.accountEvents.add({
      id: crypto.randomUUID(),
      companyId: input.companyId,
      accountNumber: null,
      kind: 'period_locked',
      fiscalYearId: input.fiscalYearId,
      at: now,
      details: {
        periodKey: input.periodKey,
        lockedThrough: input.lockedThrough,
        reason: input.reason,
        settlementVerifikationId: input.settlementVerifikationId ?? null,
      },
    });
    return lock;
  });
}
